import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import axios from "axios";

const TOKEN_KEY = "klareet_admin_token";
const EMAIL_KEY = "klareet_admin_email";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const adminApi = axios.create({
    baseURL: `${BACKEND_URL}/api/admin`,
    timeout: 20000,
    headers: { "Content-Type": "application/json" },
});

const AdminAuthContext = createContext(null);

function loadStored() {
    try {
        return {
            token: localStorage.getItem(TOKEN_KEY) || null,
            email: localStorage.getItem(EMAIL_KEY) || null,
        };
    } catch {
        return { token: null, email: null };
    }
}

export function AdminAuthProvider({ children }) {
    const [{ token, email }, setAuth] = useState(loadStored());
    const [verifying, setVerifying] = useState(Boolean(token));
    const [admin, setAdmin] = useState(null);

    const setSession = useCallback((nextToken, nextEmail) => {
        try {
            if (nextToken) {
                localStorage.setItem(TOKEN_KEY, nextToken);
                localStorage.setItem(EMAIL_KEY, nextEmail || "");
            } else {
                localStorage.removeItem(TOKEN_KEY);
                localStorage.removeItem(EMAIL_KEY);
            }
        } catch {
            // storage unavailable — auth becomes session-only
        }
        setAuth({ token: nextToken, email: nextEmail });
        if (!nextToken) setAdmin(null);
    }, []);

    const logout = useCallback(() => setSession(null, null), [setSession]);

    const verify = useCallback(async () => {
        if (!token) {
            setVerifying(false);
            setAdmin(null);
            return false;
        }
        try {
            const { data } = await adminApi.get("/me", {
                headers: { Authorization: `Bearer ${token}` },
            });
            setAdmin(data);
            return true;
        } catch (err) {
            if (err?.response?.status === 401) {
                logout();
            }
            return false;
        } finally {
            setVerifying(false);
        }
    }, [token, logout]);

    useEffect(() => {
        verify();
    }, [verify]);

    // Default Authorization header for all admin requests.
    useEffect(() => {
        if (token) {
            adminApi.defaults.headers.common["Authorization"] = `Bearer ${token}`;
        } else {
            delete adminApi.defaults.headers.common["Authorization"];
        }
    }, [token]);

    // Global 401 → logout.
    useEffect(() => {
        const id = adminApi.interceptors.response.use(
            (r) => r,
            (err) => {
                if (err?.response?.status === 401) {
                    logout();
                }
                return Promise.reject(err);
            },
        );
        return () => adminApi.interceptors.response.eject(id);
    }, [logout]);

    const value = useMemo(
        () => ({ token, email, admin, verifying, setSession, logout, verify }),
        [token, email, admin, verifying, setSession, logout, verify],
    );

    return (
        <AdminAuthContext.Provider value={value}>
            {children}
        </AdminAuthContext.Provider>
    );
}

export function useAdminAuth() {
    const ctx = useContext(AdminAuthContext);
    if (!ctx) {
        throw new Error("useAdminAuth must be used within AdminAuthProvider");
    }
    return ctx;
}
